import { z } from 'zod'
import {
  advisorVerdictSchema,
  advisorSourceSchema,
  contextItemSchema,
} from './why-panel.js'
import { docRefSchema } from './leave.js'

// ── Leave detail — GET /api/v1/leaves/{leave_id} ────────────────────────────
//
// Powers the `/leaves/:id` page (employee + manager + HR detail view). Same
// AI advisor surface as the why-panel so the persisted advisor payload is the
// single source of truth — no extra AIGW round-trip just to render this page.

export const leaveDetailEmployeeSchema = z.object({
  id: z.guid(),
  name: z.string(),
  preferredName: z.string().nullable(),
  email: z.string().email(),
  team_id: z.guid(),
  team_name: z.string(),
  role: z.string(),
  // Direct line manager's UUID — surfaced so the FE can gate the
  // "Recommend to Director" CTA on overflow composites to the direct
  // manager only. Sourced from approval_assignments via the resolver
  // (alembic 0077 dropped the legacy employees.reports_to column).
  primary_approver_id: z.guid().nullable().optional(),
  // Direct line manager's display name — lets the approver-path stepper name
  // the manager node even after the leave has escalated past them (where
  // `next_reviewer` names the Exec Director, not the manager).
  primary_approver_name: z.string().nullable().optional(),
})
export type LeaveDetailEmployee = z.infer<typeof leaveDetailEmployeeSchema>

export const leaveDetailTimelineEntrySchema = z.object({
  id: z.guid(),
  action: z.string(),
  actor_name: z.string().nullable(),
  actor_role: z.string().nullable(),
  source: z.string(),
  note: z.string().nullable(),
  created_at: z.string(),
})
export type LeaveDetailTimelineEntry = z.infer<typeof leaveDetailTimelineEntrySchema>

// Pre-generated /leave/ask analysis (Phase 2). Captured once at submit
// time by the advise_leave worker and stashed on
// `leaves.ai_advisor_payload.fullAnalysis`. The detail page renders this
// directly — no per-view streaming. None when the worker hadn't captured
// it yet (legacy leaves, transport failure); the page then falls back to
// the live SSE endpoint.
export const fullAnalysisToolCallSchema = z.object({
  tool_id: z.string(),
  summary: z.string(),
  computed_at: z.string().nullable().optional(),
  rendered_sql: z.string().nullable().optional(),
  row_count: z.number().nullable().optional(),
})
export type FullAnalysisToolCall = z.infer<typeof fullAnalysisToolCallSchema>

export const fullAnalysisPayloadSchema = z.object({
  content: z.string(),
  tool_calls: z.array(fullAnalysisToolCallSchema),
  // Verdict and rationale are now always empty strings — the rule engine
  // (algo_recommendation) owns the decision. These fields are kept for
  // back-compat with cached payloads written before this refactor.
  verdict: z.string().optional().default(''),
  rationale: z.string().optional().default(''),
})
export type FullAnalysisPayload = z.infer<typeof fullAnalysisPayloadSchema>

// Team calendar strip — rendered next to the date chip on the detail
// page. Per-day breakdown of who else on the requester's team is out,
// plus weekend / public-holiday metadata so the frontend doesn't have to
// recompute working-day arithmetic.
export const teamCalendarMemberSchema = z.object({
  employee_id: z.guid(),
  name: z.string(),
  preferred_name: z.string().nullable(),
  initials: z.string(),
  half_day: z.boolean(),
  is_emergency: z.boolean(),
  // A real leave type, or `'hidden'` when the viewer may not see this
  // teammate's type (#263) — reason is then null too.
  leave_type: z.string(),
  // Full leave range so bar tooltips show the true duration even when the
  // visible calendar window clips the bar to a shorter slice.
  leave_start_date: z.string(),
  leave_end_date: z.string(),
  is_requester: z.boolean(),
  reason: z.string().nullable().optional(),
  // Lifecycle status of this teammate's leave ("approved", "pending",
  // "escalated", …). Drives the dashed-vs-solid bar border on the
  // detail-page coverage grid: anything not yet approved is tentative.
  status: z.string().nullable().optional(),
})
export type TeamCalendarMember = z.infer<typeof teamCalendarMemberSchema>

export const teamCalendarDaySchema = z.object({
  date: z.string(),
  is_weekend: z.boolean(),
  is_public_holiday: z.boolean(),
  public_holiday_name: z.string().nullable(),
  is_in_leave_window: z.boolean(),
  out: z.array(teamCalendarMemberSchema),
})
export type TeamCalendarDay = z.infer<typeof teamCalendarDaySchema>

export const teamCalendarBlackoutSchema = z.object({
  id: z.guid(),
  name: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  level: z.string(),
})
export type TeamCalendarBlackout = z.infer<typeof teamCalendarBlackoutSchema>

export const teamCalendarPayloadSchema = z.object({
  team_size: z.number(),
  days: z.array(teamCalendarDaySchema),
  blackouts: z.array(teamCalendarBlackoutSchema).default([]),
})
export type TeamCalendarPayload = z.infer<typeof teamCalendarPayloadSchema>

// Policy v1.0 §2.3 — health-bar projection for the requesting employee.
// Surfaced on the leave detail page so approvers see the same wellbeing
// context the manager queue list rows expose. Snake-case to match the
// rest of the detail-payload schema.
export const leaveDetailHealthBarSchema = z.object({
  score: z.number().int().nonnegative(),
  band: z.enum(['low', 'medium', 'high']),
  contributing_patterns: z.array(z.string()).default([]),
})
export type LeaveDetailHealthBar = z.infer<typeof leaveDetailHealthBarSchema>

export const nextReviewerSchema = z.object({
  name: z.string().nullable(),
  role: z.string(),
})
export type NextReviewer = z.infer<typeof nextReviewerSchema>

export const leaveDetailPayloadSchema = z.object({
  id: z.guid(),
  employee: leaveDetailEmployeeSchema,
  type: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  half_day: z.boolean(),
  // Per-date half-day breakdown (migration 0057), exposed on the read path.
  // `half_days` lists the specific working days taken as a half (AM/PM);
  // dates absent from it are full days. `half_start`/`half_end` are the
  // derived first/last-day-is-a-half flags. Defaulted for back-compat with
  // fixtures / older payloads that predate the fields.
  half_start: z.boolean().default(false),
  half_end: z.boolean().default(false),
  half_days: z
    .array(z.object({ date: z.string(), slot: z.enum(['AM', 'PM']) }))
    .default([]),
  is_emergency: z.boolean(),
  // True when HR encoded this leave out of system (approved offline, then
  // recorded on the employee's behalf). Drives the "Out of system" header chip.
  // Defaulted for back-compat with fixtures / older payloads that predate it.
  is_out_of_system: z.boolean().default(false),
  status: z.string(),
  reason: z.string().nullable(),
  // Reason the requester gave when asking to cancel an approved leave.
  // Non-null while status === 'pending_cancellation'; surfaced to the
  // deciding manager. Optional/nullable for back-compat with legacy rows.
  cancel_reason: z.string().nullable().optional(),
  days: z.number(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  // Pending-state reason tag (migration 0088). "blackout_reset" when the
  // reconciler flipped this leave back to PENDING; otherwise null/undefined.
  pending_reason: z.string().nullable().optional(),
  // Manager justification (migration 0089). Populated when a manager
  // endorses a blackout-overlapping AL/SL to the Director. Surfaced on
  // the Director escalation card so the decision has context. NULL on
  // every other leave.
  manager_justification: z.string().nullable().optional(),
  // RFC 007 — days of this request that fall outside the paid balance (unpaid
  // overflow). > 0 means the leave must be escalated to the Exec Director; a
  // manager can't approve it directly. Optional/defaulted for legacy rows.
  unpaid_days: z.number().optional(),
  // RFC 007 — canonical escalation verdict from the server
  // (domain/escalation.py::leave_requires_escalation): true when the leave
  // must be endorsed up to the Exec Director rather than decided by the
  // manager. The approver-path stepper reads this instead of re-deriving the
  // unpaid/blackout trigger client-side. Optional for cached/legacy payloads.
  escalation_required: z.boolean().optional(),
  // Live capacity verdict (domain/escalation.py::capacity_breach_at_approval):
  // true when approving THIS leave now would push the team over its
  // concurrent-absence cap on some day (approved-only, per-day). Capacity no
  // longer blocks (2026-09-24): the manager CAN still approve, but the decision
  // surface raises an over-capacity CONFIRM dialog and sends `overCapacityAck`.
  // `capacity_detail` carries the snapshot for that dialog. Optional for
  // cached/legacy payloads.
  capacity_reached: z.boolean().optional(),
  // Busiest-day snapshot behind `capacity_reached` — present iff it is true, so
  // the confirm dialog can show "N+1 of cap on <day>". Null/absent within-cap.
  capacity_detail: z
    .object({
      peak_others: z.number(),
      team_cap: z.number(),
      day: z.string(),
      cap_phrase: z.string(),
    })
    .nullable()
    .optional(),
  // True when this leave was ultimately approved over capacity — the durable
  // badge on the detail page. Optional for cached/legacy payloads + fixtures;
  // consumers read `=== true`. The API always sends it (defaults to false).
  approved_over_capacity: z.boolean().optional(),
  // Requester's remaining paid balance for this leave's type, after this
  // leave counts. Computed server-side via the shared balance engine; the
  // matching balance row's `available` already nets out this leave. null
  // when the type draws no balance (unpaid), no matching row exists, or the
  // computation failed. Optional/nullable (like `unpaid_days`) so cached /
  // older payloads and fixtures that omit the field still parse; the web
  // cell renders "—" when it's null/undefined.
  balance_after: z.number().nullable().optional(),

  // The AI advisor surface (verdict / headline / bullets / sources /
  // ai_rationale), Zone 3 context_items and full_analysis were removed with the
  // leaves.ai_advisor_* columns in migration 0185. They were REQUIRED fields
  // here, so they had to go from this schema in the same change as the api —
  // a payload missing `verdict` would have failed the parse and taken the whole
  // leave-detail page down, not just the panel that read it.
  team_calendar: teamCalendarPayloadSchema.default({ team_size: 0, days: [], blackouts: [] }),

  timeline: z.array(leaveDetailTimelineEntrySchema),

  // Supporting documents attached at apply time (ADR-011). Metadata only —
  // the OCR verdict fields this comment used to describe were removed with the
  // cross-check flow (SG#7) and their columns dropped by migration 0187.
  documents: z.array(docRefSchema).default([]),

  next_reviewer: nextReviewerSchema.nullable().default(null),

  // Live priority score (recomputed every request from priority_score_rules).
  // ai_priority_score is the sum of priority_components.contribution.
  // priority_components is the per-rule breakdown shown on the detail page.
  ai_priority_score: z.number().nullable().optional(),
  ai_priority_reasons: z
    .array(z.object({ code: z.string(), label: z.string() }))
    .nullable()
    .optional(),
  priority_components: z
    .array(
      z.object({
        rule_name: z.string(),
        label: z.string(),
        contribution: z.number(),
        evidence: z.string(),
        // RFC §3.2 (slice 14): when present, UI styles chip as inactive.
        not_applicable_reason: z.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),

  // Composite-request relationship — populated when this leave is one
  // leg of an overflow split (Annual + Unpaid, Medical + Unpaid, etc.).
  // Detail page renders a header block from this so opening either side
  // makes the relationship obvious.
  composite: z
    .object({
      sibling_id: z.string(),
      sibling_type: z.string(),
      sibling_type_label: z.string(),
      this_type_label: z.string(),
      this_days: z.number(),
      sibling_days: z.number(),
      total_days: z.number(),
    })
    .nullable()
    .optional(),

  // Policy v1.0 §2.3 — health-bar projection for the requesting employee.
  // Drives the HealthBarCard rendered alongside the AI analysis on the
  // detail page so approvers see wellbeing context next to the decision.
  health_bar: leaveDetailHealthBarSchema.nullable().optional(),

  // Points at the linked sibling row (annual ↔ unpaid) when this row was
  // created via apply-with-overflow. NULL for normal single-row leaves.
  linked_request_id: z.string().nullable().optional(),

  // RFC 004 §4.3 — true after a Director approves an UNPAID leave;
  // the next payroll CSV export sweeps the row up. Surfaced here so
  // HR can confirm at a glance the row is queued for deduction.
  payroll_deduction_pending: z.boolean().optional(),
})
export type LeaveDetailPayload = z.infer<typeof leaveDetailPayloadSchema>
